import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {Page} from '@/hooks/empresa/organizacion/use-businesses'
import type {components} from '@/lib/api-schema'

type BateriaSolicitudTenant = components['schemas']['BateriaSolicitudTenantRead']
type BateriaSolicitudEnvelope = components['schemas']['BateriaSolicitudTenantEnvelope']
export type BateriaSolicitudAdmin = components['schemas']['BateriaSolicitudAdminRead']
export type BateriaConsolidadoTenant = components['schemas']['BateriaConsolidadoTenantRead']

export const BATERIA_SOLICITUDES_PAGE_SIZE = 10

/** State only, never evaluación data (Res. 2764/2022 art. 5). */
export function useBateriaSolicitud(businessId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['bateria-solicitud', businessId],
    queryFn: async () =>
      (await api.get<BateriaSolicitudEnvelope>(`/businesses/${businessId}/sgsst/bateria-solicitud`)).data.solicitud,
    enabled: enabled && !!businessId,
  })
}

/** Only group aggregates and re-application periodicity (Res. 2764/2022 art. 3); never individual data (art. 5). */
export function useBateriaConsolidado(businessId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['bateria-consolidado', businessId],
    queryFn: async () =>
      (await api.get<BateriaConsolidadoTenant>(`/businesses/${businessId}/sgsst/bateria-consolidado`)).data,
    enabled: enabled && !!businessId,
  })
}

export function useSolicitarBateria(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (nota: string | null) =>
      (await api.post<BateriaSolicitudTenant>(`/businesses/${businessId}/sgsst/bateria-solicitudes`, {nota})).data,
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['bateria-solicitud', businessId]}),
  })
}

type BateriaSolicitudEstado = BateriaSolicitudAdmin['estado']

/** bateria-checklist-spec D4: el badge «Solicitudes (N)» filtra por `estado` para contar solo pendientes. */
export function useBateriaSolicitudesAdmin(page: number, enabled: boolean, estado?: BateriaSolicitudEstado) {
  return useQuery({
    queryKey: ['admin-bateria-solicitudes', page, estado],
    queryFn: async () =>
      (
        await api.get<Page<BateriaSolicitudAdmin>>('/admin/bateria-solicitudes', {
          params: {page, page_size: BATERIA_SOLICITUDES_PAGE_SIZE, estado},
        })
      ).data,
    enabled,
    placeholderData: (previous) => previous,
  })
}

export function usePatchBateriaSolicitud() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      solicitudId: string
      action: 'atender' | 'descartar'
      evaluacion_id?: string
      motivo?: string
    }) =>
      (
        await api.patch<BateriaSolicitudAdmin>(`/admin/bateria-solicitudes/${input.solicitudId}`, {
          action: input.action,
          evaluacion_id: input.evaluacion_id ?? null,
          motivo: input.motivo ?? null,
        })
      ).data,
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['admin-bateria-solicitudes']}),
  })
}

export type BateriaInformeExterno = components['schemas']['BateriaInformeExternoRead']
export type InformeExternoCreate = components['schemas']['InformeExternoCreate']
type UploadTarget = components['schemas']['UploadTargetRead']

export const INFORMES_EXTERNOS_PAGE_SIZE = 10

export async function mintInformeExternoUploadTarget(businessId: string, file: File): Promise<UploadTarget> {
  return (
    await api.post<UploadTarget>(`/businesses/${businessId}/sgsst/bateria-informe-externo-uploads`, {
      filename: file.name,
      content_type: file.type,
      size_bytes: file.size,
    })
  ).data
}

export function useInformesExternos(businessId: string | undefined, page: number, enabled: boolean) {
  return useQuery({
    queryKey: ['bateria-informes-externos', businessId, page],
    queryFn: async () =>
      (
        await api.get<Page<BateriaInformeExterno>>(`/businesses/${businessId}/sgsst/bateria-informe-externo`, {
          params: {page, page_size: INFORMES_EXTERNOS_PAGE_SIZE},
        })
      ).data,
    enabled: enabled && !!businessId,
    placeholderData: (previous) => previous,
  })
}

function invalidarInformesExternos(queryClient: ReturnType<typeof useQueryClient>, businessId: string) {
  queryClient.invalidateQueries({queryKey: ['bateria-informes-externos', businessId]})
  queryClient.invalidateQueries({queryKey: ['bateria-consolidado', businessId]})
  // El informe puede haber entrado (o salido) como soporte del 4.1.2.
  queryClient.invalidateQueries({queryKey: ['evidence', businessId]})
  queryClient.invalidateQueries({queryKey: ['sgsst', businessId]})
}

export function useRegistrarInformeExterno(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: InformeExternoCreate) =>
      (await api.post<BateriaInformeExterno>(`/businesses/${businessId}/sgsst/bateria-informe-externo`, body)).data,
    onSuccess: () => invalidarInformesExternos(queryClient, businessId),
  })
}

export function useEliminarInformeExterno(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (informeId: string) => {
      await api.delete(`/businesses/${businessId}/sgsst/bateria-informe-externo/${informeId}`)
    },
    onSuccess: () => invalidarInformesExternos(queryClient, businessId),
  })
}
